import React, { useEffect, useRef, useState } from 'react';

import {
  Button,
  Icon,
  Separator,
  Stack,
  Text,
  Textarea,
  cn,
} from '@eluve/components';
import { FeedbackTypeEnum } from '@eluve/graphql-types';
import { type MessageKey, useTranslation } from '@eluve/i18n';

const NEGATIVE_TAGS = [
  'Too long',
  'Wrong information',
  'Too slow',
  'Not enough information',
  'Other issue',
] as const;

// Keep the canonical English tag as the stored/transmitted value (so downstream
// records stay language-independent), and map each one to its display key.
const NEGATIVE_TAG_LABEL_KEYS: Record<
  (typeof NEGATIVE_TAGS)[number],
  MessageKey
> = {
  'Too long': 'appointment.feedback.tagTooLong',
  'Wrong information': 'appointment.feedback.tagWrongInformation',
  'Too slow': 'appointment.feedback.tagTooSlow',
  'Not enough information': 'appointment.feedback.tagNotEnoughInformation',
  'Other issue': 'appointment.feedback.tagOtherIssue',
};

export interface InsightFeedbackProps {
  feedbackType: FeedbackTypeEnum | null;
  isSubmitting: boolean;
  showNegativeForm?: boolean;
  onThumbsUp: () => void;
  onThumbsDown: () => void;
  onSubmitNegative: (tags: string[], comment: string) => void;
  onCloseNegativeForm?: () => void;
}

export const InsightFeedback: React.FC<InsightFeedbackProps> = ({
  feedbackType,
  isSubmitting,
  showNegativeForm: showNegativeFormProp,
  onThumbsUp,
  onThumbsDown,
  onSubmitNegative,
  onCloseNegativeForm,
}) => {
  const { t } = useTranslation();
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [comment, setComment] = useState('');

  const handleThumbsUp = () => {
    if (isSubmitting) return;
    onThumbsUp();
  };

  const handleThumbsDown = () => {
    if (isSubmitting) return;
    onThumbsDown();
  };

  const toggleTag = (tag: string) => {
    if (isSubmitting) return;
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const handleSubmit = () => {
    if (isSubmitting || feedbackType !== 'NEGATIVE') return;
    onSubmitNegative([...selectedTags], comment);
  };

  const showNegativeForm = showNegativeFormProp ?? feedbackType === 'NEGATIVE';

  // Reset form state when negative form is closed
  const prevShowNegativeFormRef = useRef(showNegativeForm);
  useEffect(() => {
    if (prevShowNegativeFormRef.current && !showNegativeForm) {
      setSelectedTags([]);
      setComment('');
    }
    prevShowNegativeFormRef.current = showNegativeForm;
  }, [showNegativeForm]);

  return (
    <Stack align="stretch" gap={2}>
      <Separator className="my-2" />
      <Stack justify="start" align="center" gap={2} direction="horizontal">
        <Text inline color="secondary" size="xs" weight="regular">
          {t('appointment.feedback.wasHelpful')}
        </Text>
        <Button
          aria-label={t('appointment.feedback.thumbsUp')}
          size="sm"
          variant={feedbackType === 'POSITIVE' ? 'default' : 'outline'}
          disabled={isSubmitting}
          onClick={handleThumbsUp}
        >
          <Icon name="ThumbsUp" size="auto" />
        </Button>
        <Button
          aria-label={t('appointment.feedback.thumbsDown')}
          size="sm"
          variant={feedbackType === 'NEGATIVE' ? 'default' : 'outline'}
          disabled={isSubmitting}
          onClick={handleThumbsDown}
        >
          <Icon name="ThumbsDown" size="auto" />
        </Button>
      </Stack>

      {showNegativeForm && (
        <div className="rounded-lg bg-bg-secondary p-3">
          <Stack gap={3}>
            <Stack
              justify="between"
              align="center"
              gap={2}
              direction="horizontal"
            >
              <Text inline>{t('appointment.feedback.shareFeedback')}</Text>
              {onCloseNegativeForm && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onCloseNegativeForm}
                  aria-label={t('appointment.feedback.closeForm')}
                >
                  <Icon name="X" size="auto" />
                </Button>
              )}
            </Stack>
            <Stack gap={1} wrap direction="horizontal">
              {NEGATIVE_TAGS.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  disabled={isSubmitting}
                  className={cn(
                    'rounded-[20px] border px-3 py-1 text-sm transition-colors',
                    selectedTags.includes(tag)
                      ? 'border-transparent bg-bg-strong-primary text-text-strong'
                      : 'border-border-primary bg-bg-primary text-text-primary hover:bg-bg-secondary',
                  )}
                >
                  {t(NEGATIVE_TAG_LABEL_KEYS[tag])}
                </button>
              ))}
            </Stack>
            <Textarea
              rows={1}
              className="min-h-8 w-full resize-none text-text-primary outline-hidden focus-visible:ring-0"
              value={comment}
              placeholder={t('appointment.feedback.tellMoreOptional')}
              onChange={(e) => setComment(e.currentTarget.value)}
              disabled={isSubmitting}
            />
            <Stack justify="end" gap={2} direction="horizontal">
              <button
                type="button"
                disabled={isSubmitting || selectedTags.length === 0}
                onClick={handleSubmit}
                className={cn(
                  'inline-flex h-[30px] items-center justify-center rounded-md px-2 text-sm text-text-strong transition-colors',
                  isSubmitting || selectedTags.length === 0
                    ? 'cursor-not-allowed bg-bg-strong-primary opacity-70'
                    : 'bg-bg-strong-primary hover:bg-bg-strong-secondary',
                )}
              >
                {t('appointment.feedback.submitShort')}
              </button>
            </Stack>
          </Stack>
        </div>
      )}
    </Stack>
  );
};
